import { route } from 'preact-router';
import type { RoutableProps } from 'preact-router';
import { Button } from '../components/Button';

export function Privacy(_props: RoutableProps) {
  return (
    <div className="min-h-screen bg-surface-50">
      <div className="container-custom py-8">
        <div className="max-w-2xl mx-auto">
          <header className="flex items-center justify-between mb-8">
            <h1 className="text-2xl font-bold text-neutral-900">Privacy Policy</h1>
            <Button onClick={() => route('/')} variant="ghost" size="sm">
              Home
            </Button>
          </header>

          <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-8">
            <div className="prose prose-neutral max-w-none">
              <p className="text-sm text-neutral-500 mb-6">Last updated: January 2026</p>

              <h2>Children's Privacy (COPPA Compliance)</h2>
              <p>
                Storyseed is designed for use by children under 13 with parental supervision. We
                comply with the Children's Online Privacy Protection Act (COPPA) and take
                children's privacy very seriously.
              </p>

              <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 my-6">
                <h3 className="text-primary-900 mt-0">Summary for Parents</h3>
                <ul className="mb-0">
                  <li>
                    <strong>No accounts required</strong> - children use the app without signing up
                  </li>
                  <li>
                    <strong>Minimal data</strong> - only the hero's name and story choices are
                    processed
                  </li>
                  <li>
                    <strong>Local storage</strong> - stories are saved on your device, not our
                    servers
                  </li>
                  <li>
                    <strong>No advertising</strong> - we do not show ads or track for marketing
                  </li>
                  <li>
                    <strong>Full control</strong> - delete all data anytime via Settings
                  </li>
                </ul>
              </div>

              <h2>Data We Process</h2>

              <h3>Information Collected During Story Creation</h3>
              <p>When creating a story, the following information is processed:</p>
              <ul>
                <li>
                  <strong>Hero name:</strong> The name chosen for the animal character (up to 20
                  characters, letters only)
                </li>
                <li>
                  <strong>Story choices:</strong> Animal type, story type (fable/adventure/bedtime),
                  and setting
                </li>
              </ul>
              <p>
                This information is sent to our AI service provider to generate the personalized
                story. The name is used only within the story text and is not used to identify any
                child.
              </p>

              <h3>Information We Do NOT Collect</h3>
              <ul>
                <li>Email addresses, phone numbers, or physical addresses</li>
                <li>Photos, videos, or audio recordings from users</li>
                <li>Location data beyond what is technically necessary for web requests</li>
                <li>Cookies for tracking or advertising purposes</li>
                <li>Social media accounts or third-party login information</li>
              </ul>

              <h2>Third-Party Data Processors</h2>
              <p>
                We use the following third-party services to operate Storyseed. Each service
                processes data only as necessary to provide their specific function:
              </p>

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 pr-4">Service</th>
                      <th className="text-left py-2 pr-4">Purpose</th>
                      <th className="text-left py-2">Data Processed</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b">
                      <td className="py-3 pr-4">
                        <strong>Anthropic (Claude AI)</strong>
                        <br />
                        <a
                          href="https://www.anthropic.com/privacy"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600"
                        >
                          Privacy Policy
                        </a>
                      </td>
                      <td className="py-3 pr-4">Story text generation</td>
                      <td className="py-3">Hero name and story choices</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 pr-4">
                        <strong>Cloudflare</strong>
                        <br />
                        <a
                          href="https://www.cloudflare.com/privacypolicy/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600"
                        >
                          Privacy Policy
                        </a>
                      </td>
                      <td className="py-3 pr-4">Website hosting and delivery</td>
                      <td className="py-3">Standard web request data (IP address, browser type)</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 pr-4">
                        <strong>ElevenLabs</strong>
                        <br />
                        <a
                          href="https://elevenlabs.io/privacy"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600"
                        >
                          Privacy Policy
                        </a>
                      </td>
                      <td className="py-3 pr-4">Voice narration generation</td>
                      <td className="py-3">Story text content (for audio conversion)</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 pr-4">
                        <strong>Google AI (Imagen)</strong>
                        <br />
                        <a
                          href="https://policies.google.com/privacy"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600"
                        >
                          Privacy Policy
                        </a>
                      </td>
                      <td className="py-3 pr-4">Story illustration generation</td>
                      <td className="py-3">Story scene descriptions (no child names sent)</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-3 pr-4">
                        <strong>Stripe</strong>
                        <br />
                        <a
                          href="https://stripe.com/privacy"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600"
                        >
                          Privacy Policy
                        </a>
                      </td>
                      <td className="py-3 pr-4">Payment processing</td>
                      <td className="py-3">Payment card information (handled directly by Stripe)</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-sm text-neutral-600 mt-4">
                <strong>Important:</strong> Payment information is handled directly by Stripe and
                never touches our servers. We do not store credit card numbers or billing addresses.
              </p>

              <h3>Data Processing Terms</h3>
              <ul>
                <li>
                  Anthropic's terms specify that data sent through their API is not used to train
                  their AI models
                </li>
                <li>All data transmission uses HTTPS encryption</li>
                <li>We do not sell or share data with third parties for marketing purposes</li>
              </ul>

              <h2>Data Storage</h2>

              <h3>Local Storage (Your Device)</h3>
              <p>Stories are saved in your browser's local storage by default. This means:</p>
              <ul>
                <li>Stories are stored only on your device</li>
                <li>We cannot access locally stored stories</li>
                <li>Clearing browser data will delete saved stories</li>
                <li>Up to 10 stories can be saved locally</li>
              </ul>

              <h3>Shared Stories (Our Servers)</h3>
              <p>
                If you choose to share a story via a link, the story content is stored on our
                servers (Cloudflare D1 database). Shared stories:
              </p>
              <ul>
                <li>Are accessible only via the unique share link</li>
                <li>Include only the story text and hero name used in the story</li>
                <li>Can be deleted by the parent at any time</li>
                <li>Do not include device identifiers or IP addresses</li>
              </ul>

              <h2>Parental Rights Under COPPA</h2>
              <p>Parents and guardians have the right to:</p>
              <ul>
                <li>
                  <strong>Review:</strong> View all stories created in the Library section
                </li>
                <li>
                  <strong>Delete:</strong> Remove any or all stories at any time
                </li>
                <li>
                  <strong>Revoke consent:</strong> Delete all data and revoke consent via Settings
                </li>
                <li>
                  <strong>Request information:</strong> Contact us to request details about data
                  processing
                </li>
                <li>
                  <strong>Refuse further collection:</strong> Stop using the service at any time
                  with no consequences
                </li>
              </ul>

              <h2>Parent Verification</h2>
              <p>
                Before a child can create stories, parents must complete a verification step
                (solving a math problem) and provide consent. This gate is designed to ensure
                parental awareness and approval.
              </p>

              <h2>Data Retention</h2>
              <ul>
                <li>
                  <strong>Local stories:</strong> Retained until deleted by user or browser data is
                  cleared
                </li>
                <li>
                  <strong>Shared stories:</strong> Retained until deleted by the parent
                </li>
                <li>
                  <strong>Server logs:</strong> Standard web server logs are retained by Cloudflare
                  per their retention policy
                </li>
              </ul>

              <h2>Security</h2>
              <p>We implement industry-standard security measures including:</p>
              <ul>
                <li>HTTPS encryption for all data in transit</li>
                <li>No collection of sensitive personal information</li>
                <li>Rate limiting to prevent abuse</li>
                <li>Input validation and sanitization</li>
              </ul>

              <h2>Changes to This Policy</h2>
              <p>
                We will update the "Last updated" date when changes are made. For significant
                changes affecting data collection or use, we will require parents to re-consent
                before continued use.
              </p>

              <h2>Contact Us</h2>
              <p>
                For questions about this privacy policy, to exercise parental rights, or to report
                concerns:
              </p>
              <p>
                <strong>Email:</strong>{' '}
                <a href="mailto:privacy@straitstreet.co">privacy@straitstreet.co</a>
              </p>

              <div className="mt-8 pt-6 border-t border-neutral-200">
                <p className="text-sm text-neutral-500">
                  This privacy policy is designed to comply with the Children's Online Privacy
                  Protection Act (COPPA) and reflects our commitment to protecting children's
                  privacy online.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
